/**
 * Shared styling for the bordered cards used throughout the subscription modals.
 *
 * Three separate PANWDS/Tailwind quirks are handled here, all of which silently produce
 * "nothing happens" rather than an error, so keep them together and in one place:
 *
 * 1. `ssmutw-border-solid` is load-bearing and must always accompany `ssmutw-border`.
 *    Tailwind's `border` utility only emits `border-width: 1px` -- the matching
 *    `border-style: solid` normally comes from Preflight's base reset, but this app sets
 *    `corePlugins: { preflight: false }` in `tailwind.config.js` (to avoid clashing with
 *    PANWDS's own base styles). With no reset, `border-style` keeps its CSS initial value
 *    of `none`, so `border` + `border-<color>` paints nothing at all.
 *
 * 2. The colours are card-specific tokens (`--ssmu-card-*`), not the page-level
 *    `--ssmu-divider-line`. A `Card` sits on its own raised surface, so a divider colour
 *    tuned against the dark *page* background (#0D0D0D) is far too dark to read on it --
 *    `--ssmu-divider-line` (#242424) lands at only 1.13:1 against a card and is
 *    effectively invisible. The `--ssmu-card-*` scale is drawn from PANWDS's blue-tinted
 *    steel-greys so it shares the modal chrome's hue (~207deg); a neutral grey here reads
 *    as warm/brown against it. See `_theme_variables.scss` for the measured steps.
 *
 * 3. `[&&]` (a Tailwind arbitrary variant that doubles the class selector) is required on
 *    the background. PANWDS's `Card` always ships `dark:twds-bg-steel-gray-1100`, which
 *    compiles to `.dark\:twds-bg-steel-gray-1100:is(.twds-dark *)` -- two classes, so
 *    specificity (0,2,0) -- and `index.html` puts `twds-dark` on <html> permanently, so
 *    that rule is always live. A plain `ssmutw-bg-*` override is only (0,1,0) and loses.
 *    Doubling the class matches their specificity and wins on source order, without
 *    `!important` and without depending on any ancestor. (A `dark:` variant of our own
 *    would not work either: Tailwind prefixes the dark class, so `dark:ssmutw-*` compiles
 *    to `:is(.ssmutw-tw-dark *)` -- a class this app never sets -- making it dead CSS.)
 *
 * Kept as exported constants rather than repeated string literals so a future card cannot
 * reintroduce any of the above by copying only part of the recipe.
 *
 * Note there is deliberately no selected-state border variant: selection is already
 * conveyed by each card's checkbox, so recolouring the border to the blue accent was
 * redundant emphasis that made a partially-checked group look noisy.
 */
export const BORDERED_CARD_CLASS = [
    'ssmutw-border ssmutw-border-solid ssmutw-border-ssmu-card-border',
    '[&&]:ssmutw-bg-ssmu-card-bg',
].join(' ');

/**
 * Hover + click affordance for the product panel's expand/collapse header band.
 *
 * This is applied to the header *container* (the row that holds both the expand toggle and
 * the remove button), not to an inner element, so the highlight covers the full width of
 * the card and the whole band reads as one click target.
 *
 * Transitions `background-color` specifically, NOT the broader `transition-colors`.
 * `transition-colors` also animates `border-color`, which made expanding the panel flash a
 * white line: the header only gains its bottom border when open, and with no border colour
 * declared in the closed state the browser starts from `border-color: currentColor` --
 * `rgb(255 255 255)` inside the modal body -- then faded to the card border colour over
 * 150ms. Background is the only thing that should animate here.
 *
 * Deliberately no `-m-*`/`p-*` bleed pair: an earlier version sat on an inset child button
 * and needed negative margins to grow the highlight past its own box. The header band now
 * owns the card's header padding directly, so the highlight already spans edge to edge --
 * adding the bleed back would push it outside the card's rounded border.
 */
export const CARD_HEADER_HOVER_CLASS = [
    'ssmutw-rounded ssmutw-transition-[background-color] ssmutw-duration-150',
    'ssmutw-cursor-pointer hover:ssmutw-bg-ssmu-card-hover',
].join(' ');

export default BORDERED_CARD_CLASS;
